import { FC, useState } from 'react';

import { BlacklistItem } from '@shared/api/giftCoupon/giftCoupon.interface';

export interface ProductStat {
  key: string;
  /** "상품명 - 브랜드 - 선택유닛명" 평문 (select 옵션 등) */
  label: string;
  itemName: string;
  /** 브랜드명 — 미populate/미지정이면 null (태그 미노출) */
  brandName: string | null;
  /** 선택형 상품의 유닛명 — 단일 상품이면 null */
  unitName: string | null;
  count: number;
  duplicateCount: number;
  /** 발송 슬롯(10/14/18시)이 도래해 "지금 발송"해야 하는 건수 */
  dueCount: number;
  oldestRequestedAt: number;
  avgRequestedAt: number;
  /** 이 상품 대기분 전량 발송 시 예상 구매액 — 예측 미지원(구서버)이면 undefined */
  estimatedAmount?: number;
  /** 같은 대기분을 정가 감액률(오피스콘 12%·애드콘 15%)로 계산한 예상액 — 다를 때만 병기 */
  estimatedListAmount?: number;
}

function formatTime(timestampMs: number): string {
  if (!isFinite(timestampMs)) return '';
  const d = new Date(timestampMs);
  const now = new Date();
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  const isSameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (isSameDay) return `${h}:${m}`;
  const mon = d.getMonth() + 1;
  const day = d.getDate();
  return `${mon}/${day} ${h}:${m}`;
}

interface Props {
  totalPending: number;
  todayDeliveredCount: number;
  productStats: ProductStat[];
  blacklist: BlacklistItem[];
  onProductClick?: (productKey: string) => void;
}

export const RedemptionStatsBar: FC<Props> = ({
  totalPending,
  todayDeliveredCount,
  productStats,
  blacklist,
  onProductClick,
}) => {
  const totalDuplicates = productStats.reduce((sum, p) => sum + p.duplicateCount, 0);
  const [detailOpen, setDetailOpen] = useState(true);
  const [blacklistOpen, setBlacklistOpen] = useState(false);
  const [blacklistSearch, setBlacklistSearch] = useState('');

  const filteredBlacklist = blacklistSearch
    ? blacklist.filter(
        (item) =>
          item.phoneNumber.includes(blacklistSearch) ||
          item.names.some((n) => n.includes(blacklistSearch)),
      )
    : blacklist;

  return (
    <div className="space-y-2 px-4 pt-3 pb-1">
      {/* 요약 지표 - 컴팩트 인라인 */}
      <div className="flex items-center gap-4 text-sm">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-yellow-400" />
          <span className="text-gray-500">대기</span>
          <span className="font-bold text-yellow-700">{totalPending}</span>
        </span>
        <span className="text-gray-300">|</span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-green-400" />
          <span className="text-gray-500">오늘 완료</span>
          <span className="font-bold text-green-700">{todayDeliveredCount}</span>
        </span>
        {totalDuplicates > 0 && (
          <>
            <span className="text-gray-300">|</span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-red-400" />
              <span className="text-gray-500">중복 의심</span>
              <span className="font-bold text-red-600">{totalDuplicates}</span>
            </span>
          </>
        )}
        {productStats.length > 0 && (
          <>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              onClick={() => setDetailOpen((prev) => !prev)}
              className="flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            >
              상세
              <svg
                className={`h-3.5 w-3.5 transition-transform ${
                  detailOpen ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* 상품별 대기 + 중복 의심 (좌우 배치) */}
      {productStats.length > 0 && detailOpen && (
        <div className="flex gap-3">
          {/* 좌: 상품별 대기 현황 */}
          <div
            className={`rounded-lg border bg-white ${
              blacklist.length > 0 ? 'w-2/3' : 'w-full'
            }`}
          >
            <div className="border-b px-3 py-1.5">
              <span className="text-xs font-semibold text-gray-700">
                상품별 대기 ({productStats.length}종)
              </span>
            </div>
            <div className="max-h-[240px] divide-y overflow-y-auto">
              {productStats.map((product, idx) => {
                const avgTime = formatTime(product.avgRequestedAt);
                const oldestTime = formatTime(product.oldestRequestedAt);
                const isDue = product.dueCount > 0;
                return (
                  <button
                    key={product.key}
                    type="button"
                    onClick={() => onProductClick?.(product.key)}
                    className={`flex w-full items-center justify-between border-l-4 px-3 py-2 text-left transition-colors hover:bg-gray-50 ${
                      isDue ? 'border-l-orange-500' : 'border-transparent'
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-1.5 overflow-hidden">
                      <span className="shrink-0 text-xs text-gray-400">{idx + 1}</span>
                      <span className="truncate text-sm text-gray-800">
                        {product.itemName || '-'}
                      </span>
                      {product.brandName && (
                        <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
                          {product.brandName}
                        </span>
                      )}
                      {product.unitName && (
                        <span className="truncate text-sm text-gray-600">
                          {product.unitName}
                        </span>
                      )}
                    </span>
                    <span className="ml-2 flex shrink-0 items-center gap-1.5">
                      {(oldestTime || avgTime) && (
                        <span className="text-xs text-gray-400">
                          {oldestTime && (
                            <span className="font-medium text-orange-500">
                              {oldestTime}
                            </span>
                          )}
                          {oldestTime && avgTime && ' · '}
                          {avgTime && <>평균 {avgTime}</>}
                        </span>
                      )}
                      {product.estimatedAmount !== undefined &&
                        product.estimatedAmount > 0 && (
                          <span
                            className="text-xs font-medium text-indigo-600"
                            title="이 상품 대기분 전량 발송 시 예상 금액 — 앞: 최근 발송가 기준, 뒤: 정가 감액 기준(오피스콘 12%·애드콘 15%)"
                          >
                            예상 {product.estimatedAmount.toLocaleString()}원
                            {product.estimatedListAmount !== undefined &&
                              product.estimatedListAmount !== product.estimatedAmount && (
                                <span className="ml-1 font-normal text-gray-500">
                                  / 정가 {product.estimatedListAmount.toLocaleString()}원
                                </span>
                              )}
                          </span>
                        )}
                      <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-bold text-yellow-800">
                        {product.count}
                      </span>
                      {product.duplicateCount > 0 && (
                        <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">
                          중복{product.duplicateCount}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 우: 중복 의심 번호 (접기/펼치기) */}
          {blacklist.length > 0 && (
            <div className="w-1/3 rounded-lg border border-red-200 bg-red-50">
              <button
                type="button"
                onClick={() => setBlacklistOpen((prev) => !prev)}
                className="flex w-full items-center justify-between px-3 py-1.5"
              >
                <span className="text-xs font-semibold text-red-700">
                  중복 의심 번호 ({blacklist.length})
                </span>
                <span className="text-xs text-red-400">
                  {blacklistOpen ? '접기 ▲' : '펼치기 ▼'}
                </span>
              </button>

              {blacklistOpen && (
                <div className="border-t border-red-200">
                  <div className="px-3 py-1.5">
                    <input
                      type="text"
                      value={blacklistSearch}
                      onChange={(e) => setBlacklistSearch(e.target.value)}
                      placeholder="번호 또는 이름 검색"
                      className="w-full rounded border border-red-200 bg-white px-2 py-1 text-xs text-red-800 placeholder-red-300 outline-none focus:border-red-400"
                    />
                  </div>
                  <div className="max-h-[180px] divide-y divide-red-100 overflow-y-auto">
                    {filteredBlacklist.length > 0 ? (
                      filteredBlacklist.map((item) => (
                        <div
                          key={item.phoneNumber}
                          className="flex items-center justify-between px-3 py-1.5"
                        >
                          <span className="text-xs font-medium text-red-800">
                            {item.phoneNumber}
                          </span>
                          <span className="text-xs text-red-600">
                            {item.count}회 · {item.names.join(', ')}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-center text-xs text-red-400">
                        검색 결과 없음
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
